import React from 'react';
import { createPortal } from 'react-dom';
import { MeetingSlot, SpeakerProfile } from '../../../lib/api';
import { getCurrentTimezone, dateToFormattedString, DateFnsFormats, formatTimeWithUppercasePeriod } from '../../../lib/helpers/dateHelper';
import { RegistrationResult, RegistrationResultType } from '../../../hooks/use-booking-subscription';
import { FailureType } from '../../../lib/constants';
import { formatSpeakerNames } from './speakerDisplayUtils';
import BookingFailureContent from './BookingFailureContent';
import BookingSuccessContent from './BookingSuccessContent';
import './BookingResultModal.scss';

interface BookingResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  slot: MeetingSlot | null;
  speakers: SpeakerProfile[];
  showLocalTime: boolean;
  eventTimeZone: string;
  result?: RegistrationResult;
  isCancellation?: boolean;
  primaryColor?: string | null;
  transformedTimeZone?: string;
  failureType?: FailureType;
}

const BookingResultModal: React.FC<BookingResultModalProps> = ({
  isOpen,
  onClose,
  slot,
  speakers,
  eventTimeZone,
  showLocalTime,
  result = RegistrationResultType.BOOKED,
  isCancellation: isCancellationProp = false,
  primaryColor,
  transformedTimeZone = '',
  failureType
}) => {
  const isSuccess = result === RegistrationResultType.BOOKED || result === RegistrationResultType.REMOVED;
  const isCancellation = result === RegistrationResultType.REMOVED || isCancellationProp;
  const hasValidSuccessData = isSuccess && slot && speakers && speakers.length > 0;

  if (!isOpen) {
    return null;
  }

  if (isSuccess && !isCancellation && !hasValidSuccessData) {
    return null;
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getSuccessContentProps = () => {
    if (!slot || !speakers) return null;

    const currentTimeZone = getCurrentTimezone();

    return {
      formattedSpeakerNames: formatSpeakerNames(speakers),
      formattedDate: dateToFormattedString(slot.startDateTime, DateFnsFormats.StringifiedDayMonthNumericDateYear, eventTimeZone),
      startTimeEvent: formatTimeWithUppercasePeriod(slot.startDateTime, eventTimeZone),
      endTimeEvent: formatTimeWithUppercasePeriod(slot.endDateTime, eventTimeZone),
      startTimeLocal: formatTimeWithUppercasePeriod(slot.startDateTime, currentTimeZone),
      endTimeLocal: formatTimeWithUppercasePeriod(slot.endDateTime, currentTimeZone)
    };
  };

  const successProps = hasValidSuccessData ? getSuccessContentProps() : null;

  const modalContent = isSuccess
    ? (
      <BookingSuccessContent
        onClose={onClose}
        onOverlayClick={handleOverlayClick}
        formattedSpeakerNames={successProps?.formattedSpeakerNames || []}
        formattedDate={successProps?.formattedDate || ''}
        startTimeEvent={successProps?.startTimeEvent || ''}
        endTimeEvent={successProps?.endTimeEvent || ''}
        transformedTimeZone={transformedTimeZone}
        showLocalTime={showLocalTime}
        startTimeLocal={successProps?.startTimeLocal || ''}
        endTimeLocal={successProps?.endTimeLocal || ''}
        isCancellation={isCancellation}
        primaryColor={primaryColor}
      />
    )
    : (
      <BookingFailureContent
        onClose={onClose}
        onOverlayClick={handleOverlayClick}
        result={result}
        isCancellation={isCancellationProp}
        primaryColor={primaryColor}
        failureType={failureType}
      />
    );

  return createPortal(modalContent, document.body);
};

export default BookingResultModal;

